(function attachEaEnergyReport(root, factory) {
  const api = factory(root || {});
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.EaEnergyReport = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function createEaEnergyReport(root) {
  const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#ea580c', '#0f766e', '#c026d3', '#65a30d'];
  const state = {
    context: null,
    report: null,
    loadingPromise: null,
    originalPanelHtml: null,
    panelCaptured: false,
    charts: []
  };

  function uniqueStrings(values) {
    return [...new Set((values || [])
      .filter(value => value !== null && value !== undefined && String(value).trim() !== '')
      .map(value => String(value)))];
  }

  function asDeviceMap(deviceGroups) {
    if (deviceGroups instanceof Map) return deviceGroups;
    return new Map(Object.entries(deviceGroups || {}).map(([id, group]) => [String(id), group]));
  }

  function toNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (value === null || value === undefined || value === '') return null;
    let normalized = String(value).trim().replace(/\s/g, '');
    if (normalized.includes(',') && normalized.includes('.')) normalized = normalized.replace(/\./g, '').replace(',', '.');
    else normalized = normalized.replace(',', '.');
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function getRecordField(record, names) {
    if (!record || typeof record !== 'object') return null;
    const keys = new Map(Object.keys(record).map(key => [key.toLowerCase(), key]));
    for (const name of names) {
      const realKey = keys.get(String(name).toLowerCase());
      if (realKey !== undefined) return record[realKey];
    }
    return null;
  }

  function normalizeSummaryRecord(record) {
    const first = toNumber(getRecordField(record, ['first_value', 'first', 'start_value']));
    const last = toNumber(getRecordField(record, ['last_value', 'last', 'end_value']));
    let diff = toNumber(getRecordField(record, ['diff_value', 'difference', 'diff', 'delta_value']));
    if (diff === null && first !== null && last !== null) diff = last - first;
    return {
      first,
      last,
      diff,
      avg: toNumber(getRecordField(record, ['avg_value', 'average_value', 'avg', 'mean_value'])),
      min: toNumber(getRecordField(record, ['min_value', 'minimum_value', 'min'])),
      max: toNumber(getRecordField(record, ['max_value', 'maximum_value', 'max'])),
      maxTime: getRecordField(record, ['max_time', 'max_timestamp', 'maximum_time', 'max_date', 'timestamp_of_max']),
      count: toNumber(getRecordField(record, ['count', 'sample_count', 'record_count']))
    };
  }

  function normalizeSummaryPayload(payload) {
    if (!payload || typeof payload !== 'object') return {};
    let source = payload;
    if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) source = payload.data;
    const result = {};
    Object.entries(source).forEach(([tagName, record]) => {
      if (['success', 'error', 'message', 'warnings'].includes(String(tagName).toLowerCase())) return;
      if (record && typeof record === 'object' && !Array.isArray(record)) result[tagName] = normalizeSummaryRecord(record);
    });
    return result;
  }

  function powerFactorToKw(tag) {
    const rawUnit = String(tag && (tag.Birim || tag.Unit || tag.unit || tag.EngineeringUnit) || '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z]/g, '');
    if (rawUnit === 'W' || rawUnit === 'WATT' || rawUnit === 'WATTS') return 0.001;
    if (rawUnit === 'MW' || rawUnit === 'MEGAWATT' || rawUnit === 'MEGAWATTS') return 1000;
    return 1;
  }

  function buildDeviceDefinitions(deviceGroups, deviceIds, model) {
    const groups = asDeviceMap(deviceGroups);
    return uniqueStrings(deviceIds).map(deviceId => {
      const group = groups.get(String(deviceId));
      if (!group) return null;
      const powerSource = model && typeof model.resolvePowerSource === 'function'
        ? model.resolvePowerSource(group)
        : { mode: 'total', tags: [] };
      const energyTag = model && typeof model.resolveEnergySource === 'function'
        ? model.resolveEnergySource(group)
        : null;
      const powerTags = uniqueStrings(powerSource.tags);
      const groupTags = Array.isArray(group.tags) ? group.tags : [];
      const powerFactors = Object.fromEntries(powerTags.map(tagName => {
        const tag = groupTags.find(item => String(item.TagName || '') === tagName);
        return [tagName, powerFactorToKw(tag)];
      }));
      return {
        deviceId: String(deviceId),
        label: String(group.groupName || group.tagPrefix || `EA ${deviceId}`),
        code: String(group.tagPrefix || `EA ${deviceId}`),
        energyTag: energyTag || null,
        powerMode: powerSource.mode,
        powerTags,
        powerFactors,
        tags: groupTags
      };
    }).filter(Boolean);
  }

  function buildDeviceMetrics(definitions, currentSummary, previousSummary, completenessByTag, options = {}) {
    return (definitions || []).map(definition => {
      const energy = definition.energyTag ? currentSummary[definition.energyTag] : null;
      const previousEnergy = definition.energyTag ? previousSummary[definition.energyTag] : null;
      const resetDetected = !!(energy && energy.diff !== null && energy.diff < 0);
      const meterConsumptionKwh = energy && energy.diff !== null && energy.diff >= 0 ? energy.diff : null;
      const meterPreviousKwh = previousEnergy && previousEnergy.diff !== null && previousEnergy.diff >= 0 ? previousEnergy.diff : null;
      const powerFallback = options.powerFallbackByDevice && options.powerFallbackByDevice[definition.deviceId];
      const previousPowerFallback = options.previousPowerFallbackByDevice && options.previousPowerFallbackByDevice[definition.deviceId];
      const consumptionKwh = meterConsumptionKwh !== null ? meterConsumptionKwh : powerFallback && powerFallback.energyKwh !== null ? powerFallback.energyKwh : null;
      const previousKwh = meterPreviousKwh !== null ? meterPreviousKwh : previousPowerFallback && previousPowerFallback.energyKwh !== null ? previousPowerFallback.energyKwh : null;
      const powerRecords = definition.powerTags.map(tag => ({
        tag,
        factor: definition.powerFactors && definition.powerFactors[tag] || 1,
        record: currentSummary[tag]
      })).filter(item => item.record);
      const averagePowerKw = powerRecords.length && powerRecords.every(item => item.record.avg !== null)
        ? powerRecords.reduce((sum, item) => sum + item.record.avg * item.factor, 0)
        : null;
      const peakPowerKw = powerRecords.length && powerRecords.every(item => item.record.max !== null)
        ? powerRecords.reduce((sum, item) => sum + item.record.max * item.factor, 0)
        : null;
      const maxTimeRecord = powerRecords.find(item => item.record.maxTime);
      const meterCompleteness = definition.energyTag && completenessByTag[definition.energyTag] !== undefined
        ? completenessByTag[definition.energyTag]
        : null;
      return {
        ...definition,
        startIndex: energy ? energy.first : null,
        endIndex: energy ? energy.last : null,
        consumptionKwh,
        previousKwh,
        averagePowerKw,
        peakPowerKw,
        peakTime: maxTimeRecord ? maxTimeRecord.record.maxTime : null,
        completeness: meterConsumptionKwh !== null ? meterCompleteness : powerFallback ? powerFallback.completeness : meterCompleteness,
        energySource: meterConsumptionKwh !== null ? 'meter' : powerFallback && powerFallback.energyKwh !== null ? 'power' : 'missing',
        resetDetected,
        missingEnergyTag: !definition.energyTag,
        missingPowerTag: definition.powerTags.length === 0
      };
    });
  }

  function normalizeAggregationRows(payload) {
    if (Array.isArray(payload)) return payload;
    return payload && Array.isArray(payload.data) ? payload.data : [];
  }

  function aggregationTagName(row) {
    return getRecordField(row, ['tag_name', 'tagname', 'tag', 'TagName']);
  }

  function aggregationBucket(row) {
    return getRecordField(row, ['bucket', 'time_bucket', 'date', 'timestamp', 'Bucket']);
  }

  function aggregationDiff(row) {
    return toNumber(getRecordField(row, ['diff_value', 'difference', 'diff', 'delta_value']));
  }

  function aggregationAverage(row) {
    return toNumber(getRecordField(row, ['avg_value', 'average_value', 'avg', 'mean_value', 'value']));
  }

  function buildDailyTrend(rows, energyTags, expectedDays) {
    const tags = new Set(uniqueStrings(energyTags));
    const buckets = new Map();
    const seenByTag = {};
    tags.forEach(tag => { seenByTag[tag] = new Set(); });

    (rows || []).forEach(row => {
      const tag = String(aggregationTagName(row) || '');
      if (!tags.has(tag)) return;
      const bucketRaw = aggregationBucket(row);
      if (!bucketRaw) return;
      const bucket = String(bucketRaw).slice(0, 10);
      const diff = aggregationDiff(row);
      if (diff === null || diff < 0) return;
      buckets.set(bucket, (buckets.get(bucket) || 0) + diff);
      seenByTag[tag].add(bucket);
    });

    const completenessByTag = {};
    tags.forEach(tag => {
      completenessByTag[tag] = expectedDays > 0
        ? Math.min(100, seenByTag[tag].size / expectedDays * 100)
        : null;
    });
    return {
      points: [...buckets.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, value]) => ({ date, value })),
      completenessByTag
    };
  }

  function buildPowerConsumption(rows, definitions, expectedDays) {
    const definitionByTag = new Map();
    const bucketByDevice = new Map();
    (definitions || []).forEach(definition => {
      (definition.powerTags || []).forEach(tag => definitionByTag.set(tag, definition));
      bucketByDevice.set(String(definition.deviceId), new Map());
    });

    (rows || []).forEach(row => {
      const tag = String(aggregationTagName(row) || '');
      const definition = definitionByTag.get(tag);
      if (!definition) return;
      const rawBucket = aggregationBucket(row);
      const rawAverage = aggregationAverage(row);
      if (!rawBucket || rawAverage === null) return;
      const bucket = String(rawBucket).slice(0, 13);
      const deviceBuckets = bucketByDevice.get(String(definition.deviceId));
      if (!deviceBuckets.has(bucket)) deviceBuckets.set(bucket, new Map());
      const factor = definition.powerFactors && definition.powerFactors[tag] || 1;
      deviceBuckets.get(bucket).set(tag, rawAverage * factor);
    });

    const byDevice = {};
    const dailyBuckets = new Map();
    bucketByDevice.forEach((deviceBuckets, deviceId) => {
      if (!deviceBuckets.size) {
        byDevice[deviceId] = { energyKwh: null, completeness: 0, hours: 0, daily: [] };
        return;
      }
      let energyKwh = 0;
      const daily = new Map();
      deviceBuckets.forEach((values, bucket) => {
        const powerKw = [...values.values()].reduce((sum, value) => sum + value, 0);
        const hourEnergy = Math.max(0, powerKw);
        energyKwh += hourEnergy;
        const date = bucket.slice(0, 10);
        daily.set(date, (daily.get(date) || 0) + hourEnergy);
        dailyBuckets.set(date, (dailyBuckets.get(date) || 0) + hourEnergy);
      });
      byDevice[deviceId] = {
        energyKwh,
        completeness: expectedDays > 0 ? Math.min(100, deviceBuckets.size / (expectedDays * 24) * 100) : null,
        hours: deviceBuckets.size,
        daily: [...daily.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, value]) => ({ date, value }))
      };
    });
    return {
      byDevice,
      points: [...dailyBuckets.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, value]) => ({ date, value }))
    };
  }

  function mergeTrendPoints(...series) {
    const byDate = new Map();
    series.flat().forEach(point => {
      if (!point || !point.date || toNumber(point.value) === null) return;
      byDate.set(point.date, (byDate.get(point.date) || 0) + Number(point.value));
    });
    return [...byDate.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, value]) => ({ date, value }));
  }

  function buildBreakdownRows(entries, deviceMetrics) {
    const metricMap = new Map((deviceMetrics || []).map(metric => [String(metric.deviceId), metric]));
    const claimed = new Set();
    const rows = [];
    (entries || []).forEach((entry, index) => {
      const ids = uniqueStrings(entry.deviceIds).filter(id => metricMap.has(id) && !claimed.has(id));
      ids.forEach(id => claimed.add(id));
      const values = ids.map(id => metricMap.get(id).consumptionKwh).filter(value => value !== null);
      rows.push({
        label: String(entry.label || 'Alt Grup'),
        deviceIds: ids,
        deviceCount: ids.length,
        consumptionKwh: values.length ? values.reduce((sum, value) => sum + value, 0) : null,
        color: COLORS[index % COLORS.length]
      });
    });
    const unclaimed = (deviceMetrics || []).filter(metric => !claimed.has(String(metric.deviceId)));
    if (unclaimed.length) {
      const values = unclaimed.map(metric => metric.consumptionKwh).filter(value => value !== null);
      rows.push({
        label: entries && entries.length ? 'Doğrudan bağlı analizörler' : 'Analizörler',
        deviceIds: unclaimed.map(metric => metric.deviceId),
        deviceCount: unclaimed.length,
        consumptionKwh: values.length ? values.reduce((sum, value) => sum + value, 0) : null,
        color: COLORS[rows.length % COLORS.length]
      });
    }
    const total = (deviceMetrics || []).reduce((sum, metric) => sum + (metric.consumptionKwh || 0), 0);
    rows.forEach(row => { row.percent = total > 0 && row.consumptionKwh !== null ? row.consumptionKwh / total * 100 : 0; });
    return rows;
  }

  function summarizeReport(options) {
    const deviceMetrics = options.deviceMetrics || [];
    const values = deviceMetrics.map(metric => metric.consumptionKwh).filter(value => value !== null);
    const previousValues = deviceMetrics.map(metric => metric.previousKwh).filter(value => value !== null);
    const averageValues = deviceMetrics.map(metric => metric.averagePowerKw).filter(value => value !== null);
    const peaks = deviceMetrics.filter(metric => metric.peakPowerKw !== null).sort((a, b) => b.peakPowerKw - a.peakPowerKw);
    const completenessValues = deviceMetrics.map(metric => metric.completeness).filter(value => value !== null);
    const totalKwh = values.length ? values.reduce((sum, value) => sum + value, 0) : null;
    const previousKwh = previousValues.length ? previousValues.reduce((sum, value) => sum + value, 0) : null;
    return {
      totalKwh,
      previousKwh,
      changePercent: totalKwh !== null && previousKwh > 0 ? (totalKwh - previousKwh) / previousKwh * 100 : null,
      averagePowerKw: averageValues.length ? averageValues.reduce((sum, value) => sum + value, 0) : null,
      peakDevice: peaks[0] || null,
      enpiKwhPerDay: totalKwh !== null && options.periodDays > 0 ? totalKwh / options.periodDays : null,
      completeness: completenessValues.length
        ? completenessValues.reduce((sum, value) => sum + value, 0) / completenessValues.length
        : null,
      availableEnergyDevices: values.length,
      meterDevices: deviceMetrics.filter(metric => metric.energySource === 'meter').length,
      estimatedDevices: deviceMetrics.filter(metric => metric.energySource === 'power').length,
      deviceCount: deviceMetrics.length
    };
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char]);
  }

  function formatNumber(value, digits = 1) {
    if (value === null || value === undefined || value === '') return '—';
    const number = Number(value);
    return Number.isFinite(number)
      ? number.toLocaleString('tr-TR', { minimumFractionDigits: digits, maximumFractionDigits: digits })
      : '—';
  }

  function formatDate(value) {
    if (!value) return '—';
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}.${match[2]}.${match[1]}`;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('tr-TR');
  }

  function formatDateTime(value) {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('tr-TR');
  }

  function dateToInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function parseInputDate(value) {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
  }

  function addDays(date, days) {
    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    result.setDate(result.getDate() + days);
    return result;
  }

  function daysInclusive(from, to) {
    const start = parseInputDate(from);
    const end = parseInputDate(to);
    if (!start || !end || end < start) return 0;
    return Math.round((end - start) / 86400000) + 1;
  }

  function apiDate(value) {
    return `${value}T00:00:00`;
  }

  function authHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const token = root.Auth && typeof root.Auth.getToken === 'function'
        ? root.Auth.getToken()
        : root.localStorage && root.localStorage.getItem('auth_token');
      if (token) headers.Authorization = `Bearer ${token}`;
    } catch (_) { /* no-op */ }
    return headers;
  }

  async function fetchJson(url, body) {
    const response = await root.fetch(url, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
    if (!response.ok) {
      const message = await response.text().catch(() => '');
      throw new Error(`Rapor servisi ${response.status}${message ? `: ${message}` : ''}`);
    }
    const payload = await response.json();
    if (payload && payload.success === false) throw new Error(payload.error || 'Rapor verisi alınamadı.');
    return payload;
  }

  function chunks(values, size) {
    const result = [];
    for (let index = 0; index < values.length; index += size) result.push(values.slice(index, index + size));
    return result;
  }

  async function fetchSummary(tags, from, to) {
    const result = {};
    for (const part of chunks(uniqueStrings(tags), 50)) {
      const payload = await fetchJson('/api/v1/report/summary', { tags: part, from, to });
      Object.assign(result, normalizeSummaryPayload(payload));
    }
    return result;
  }

  async function fetchDailyAggregation(tags, from, to) {
    const rows = [];
    for (const part of chunks(uniqueStrings(tags), 50)) {
      const payload = await fetchJson('/api/v1/report/aggregation', {
        tags: part,
        from,
        to,
        interval: '1 day',
        aggregations: ['diff'],
        pivot: false
      });
      rows.push(...normalizeAggregationRows(payload));
    }
    return rows;
  }

  async function fetchHourlyPowerAggregation(tags, from, to) {
    const rows = [];
    for (const part of chunks(uniqueStrings(tags), 50)) {
      const payload = await fetchJson('/api/v1/report/aggregation', {
        tags: part,
        from,
        to,
        interval: '1 hour',
        aggregations: ['avg'],
        pivot: false
      });
      rows.push(...normalizeAggregationRows(payload));
    }
    return rows;
  }

  function showToast(message, options) {
    if (typeof root.showToast === 'function') root.showToast(message, options);
  }

  function installStyles() {
    if (!root.document || root.document.getElementById('eaEnergyReportStyles')) return;
    const style = root.document.createElement('style');
    style.id = 'eaEnergyReportStyles';
    style.textContent = `
      #tablePanel.ea-report-mode { color:var(--cui-body-color, #e4e6eb) !important; padding:1rem !important; background:var(--cui-tertiary-bg); }
      .ea-report-app { max-width:1280px; margin:0 auto; }
      .ea-report-controls { display:flex; flex-wrap:wrap; align-items:end; gap:.7rem; margin:0 auto 1rem; padding:.85rem 1rem; border:1px solid var(--dr-surface-border); border-radius:10px; background:var(--dr-surface-bg); }
      .ea-report-controls label { display:block; margin-bottom:.25rem; color:var(--dr-surface-muted-color); font-size:.72rem; font-weight:600; }
      .ea-report-controls .ea-report-field { min-width:150px; }
      .ea-report-controls .ea-report-check { display:flex; align-items:center; gap:.4rem; min-height:31px; color:var(--dr-surface-muted-color); font-size:.78rem; }
      .ea-report-controls .ea-report-check input { accent-color:#2563eb; }
      .ea-report-actions { display:flex; flex-wrap:wrap; gap:.45rem; margin-left:auto; }
      .ea-report-status { min-height:1.25rem; margin:.25rem 0 .75rem; color:var(--dr-surface-muted-color); font-size:.78rem; text-align:center; }
      .ea-report-status.error { color:var(--cui-danger-text-emphasis); }
      .ea-report-loader { display:none; padding:3rem; text-align:center; color:var(--dr-surface-muted-color); }
      .ea-report-app.loading .ea-report-loader { display:block; }
      .ea-report-app.loading .ea-report-document { display:none; }
      .ea-report-document { width:min(100%, 210mm); min-height:297mm; margin:0 auto 2rem; padding:14mm 13mm; background:#fff; color:#172033; box-shadow:0 16px 45px rgba(0,0,0,.32); font-family:Arial, Helvetica, sans-serif; }
      .ea-report-document * { box-sizing:border-box; }
      .ea-report-head { display:grid; grid-template-columns:1fr auto; gap:1rem; align-items:start; padding-bottom:8mm; border-bottom:2px solid #173b67; }
      .ea-report-brand { color:#173b67; font-size:12px; font-weight:800; letter-spacing:.08em; }
      .ea-report-head h1 { margin:5px 0 3px; color:#102a4c; font-size:22px; line-height:1.15; }
      .ea-report-scope { color:#526277; font-size:12px; }
      .ea-report-badge { display:inline-block; padding:6px 9px; border:1px solid #9ec1e7; border-radius:6px; background:#eef6ff; color:#174f85; font-size:9px; font-weight:700; text-align:center; }
      .ea-report-meta { display:grid; grid-template-columns:repeat(4, 1fr); gap:8px; margin:6mm 0; }
      .ea-report-meta > div, .ea-report-kpi { padding:9px 10px; border:1px solid #dbe3ec; border-radius:7px; background:#f8fafc; }
      .ea-report-meta span, .ea-report-kpi span { display:block; color:#66758a; font-size:8px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; }
      .ea-report-meta strong { display:block; margin-top:3px; color:#1c2c40; font-size:10px; }
      .ea-report-section { margin-top:7mm; page-break-inside:avoid; }
      .ea-report-section h2 { margin:0 0 3mm; padding-bottom:2mm; border-bottom:1px solid #dce4ed; color:#173b67; font-size:14px; }
      .ea-report-kpis { display:grid; grid-template-columns:repeat(4, 1fr); gap:8px; }
      .ea-report-kpi strong { display:block; margin-top:4px; color:#102a4c; font-size:16px; line-height:1.15; }
      .ea-report-kpi small { display:block; margin-top:3px; color:#7a8798; font-size:8px; }
      .ea-report-kpi.good strong { color:#16803a; }
      .ea-report-kpi.bad strong { color:#c53030; }
      .ea-report-chart-grid { display:grid; grid-template-columns:1.2fr .8fr; gap:10px; }
      .ea-report-chart-card { min-width:0; min-height:250px; overflow:hidden; padding:10px; border:1px solid #dbe3ec; border-radius:7px; background:#fff; }
      .ea-report-chart-card h3 { margin:0 0 6px; color:#44556b; font-size:10px; }
      .ea-report-chart-wrap { position:relative; height:215px; }
      .ea-report-chart-wrap canvas { max-width:100% !important; }
      .ea-report-table { width:100%; border-collapse:collapse; font-size:8.2px; }
      .ea-report-table th, .ea-report-table td { padding:6px 5px; border:1px solid #dbe3ec; vertical-align:middle; }
      .ea-report-table th { background:#173b67; color:#fff; font-size:7.5px; text-align:left; }
      .ea-report-table td.num, .ea-report-table th.num { text-align:right; font-variant-numeric:tabular-nums; }
      .ea-report-table tbody tr:nth-child(even) { background:#f7f9fc; }
      .ea-report-warning { display:flex; gap:7px; margin:5px 0; padding:7px 9px; border-left:3px solid #d59b1a; background:#fff7e2; color:#76550f; font-size:8.5px; }
      .ea-report-note { padding:9px 10px; border:1px solid #dbe3ec; border-radius:7px; background:#f8fafc; color:#536277; font-size:8.5px; line-height:1.5; }
      .ea-report-signatures { display:grid; grid-template-columns:repeat(3, 1fr); gap:18px; margin-top:13mm; }
      .ea-report-signature { padding-top:10mm; border-top:1px solid #95a2b2; color:#526277; font-size:8px; text-align:center; }
      .ea-report-footer { display:flex; justify-content:space-between; margin-top:10mm; padding-top:3mm; border-top:1px solid #dbe3ec; color:#7b8795; font-size:7px; }
      .ea-report-empty { padding:18mm 8mm; color:#718096; text-align:center; }
      .ea-report-exporting { width:210mm !important; box-shadow:none !important; }
      .ea-report-pdf-host { position:absolute; top:0; left:-12000px; width:210mm; pointer-events:none; }
      .ea-report-pdf-page { position:relative; width:210mm !important; height:297mm !important; min-height:297mm !important; max-height:297mm !important; overflow:hidden; margin:0 !important; padding:12mm 13mm 17mm !important; background:#fff !important; box-shadow:none !important; }
      .ea-report-pdf-content { display:flow-root; }
      .ea-report-page-heading { display:flex; justify-content:space-between; align-items:center; min-height:10mm; padding-bottom:3mm; border-bottom:1px solid #dce4ed; color:#173b67; font-size:9px; font-weight:700; }
      .ea-report-page-heading span:last-child { color:#66758a; font-size:7px; font-weight:400; }
      .ea-report-page-footer { position:absolute; right:13mm; bottom:7mm; left:13mm; display:flex; justify-content:space-between; gap:8px; padding-top:3mm; border-top:1px solid #dbe3ec; color:#7b8795; font-size:7px; }
      @media (max-width:900px) {
        .ea-report-meta, .ea-report-kpis { grid-template-columns:repeat(2, 1fr); }
        .ea-report-chart-grid { grid-template-columns:1fr; }
        .ea-report-actions { width:100%; margin-left:0; }
        .ea-report-document { padding:8mm 6mm; }
      }
      @media print {
        .ea-report-pdf-page { break-after:page; page-break-after:always; }
        .ea-report-pdf-page:last-child { break-after:auto; page-break-after:auto; }
      }
    `;
    root.document.head.appendChild(style);
  }

  function captureOriginalPanel() {
    if (!root.document || state.panelCaptured) return;
    const panel = root.document.getElementById('tablePanel');
    if (!panel) return;
    state.originalPanelHtml = panel.innerHTML;
    state.panelCaptured = true;
  }

  function destroyCharts() {
    state.charts.forEach(chart => { try { chart.destroy(); } catch (_) { /* no-op */ } });
    state.charts = [];
  }

  function reportShellHtml() {
    return `
      <div class="ea-report-app" id="eaEnergyReportApp">
        <div class="ea-report-controls">
          <div class="ea-report-field">
            <label for="eaReportPreset">Dönem</label>
            <select class="form-select form-select-sm" id="eaReportPreset">
              <option value="this-month">Bu ay</option>
              <option value="last-month">Geçen ay</option>
              <option value="last-30">Son 30 gün</option>
              <option value="custom">Özel tarih</option>
            </select>
          </div>
          <div class="ea-report-field"><label for="eaReportFrom">Başlangıç</label><input class="form-control form-control-sm" type="date" id="eaReportFrom"></div>
          <div class="ea-report-field"><label for="eaReportTo">Bitiş</label><input class="form-control form-control-sm" type="date" id="eaReportTo"></div>
          <label class="ea-report-check"><input type="checkbox" id="eaReportCompare" checked> Önceki dönemle karşılaştır</label>
          <div class="ea-report-actions">
            <button class="btn btn-sm btn-primary" id="eaReportGenerate"><i class="fas fa-sync-alt me-1"></i>Raporu Oluştur</button>
            <button class="btn btn-sm btn-outline-danger" id="eaReportPdf" disabled><i class="fas fa-file-pdf me-1"></i>PDF</button>
            <button class="btn btn-sm btn-outline-success" id="eaReportExcel" disabled><i class="fas fa-file-excel me-1"></i>Excel</button>
            <button class="btn btn-sm btn-outline-secondary" id="eaReportPrint" disabled><i class="fas fa-print me-1"></i>Yazdır</button>
          </div>
        </div>
        <div class="ea-report-status" id="eaReportStatus">Rapor sekmesi açıldığında seçili dönem otomatik hazırlanır.</div>
        <div class="ea-report-loader"><div class="spinner-border text-primary" role="status"></div><div class="mt-2">Enerji performans verileri hazırlanıyor…</div></div>
        <div class="ea-report-document" id="eaReportDocument"><div class="ea-report-empty">Rapor oluşturulmayı bekliyor.</div></div>
      </div>`;
  }

  function setPreset(name) {
    const fromInput = root.document.getElementById('eaReportFrom');
    const toInput = root.document.getElementById('eaReportTo');
    if (!fromInput || !toInput) return;
    const today = new Date();
    let from = new Date(today.getFullYear(), today.getMonth(), 1);
    let to = today;
    if (name === 'last-month') {
      from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      to = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (name === 'last-30') {
      from = addDays(today, -29);
    } else if (name === 'custom') {
      fromInput.disabled = false;
      toInput.disabled = false;
      return;
    }
    fromInput.value = dateToInput(from);
    toInput.value = dateToInput(to);
    fromInput.disabled = true;
    toInput.disabled = true;
  }

  function setBusy(busy) {
    const app = root.document && root.document.getElementById('eaEnergyReportApp');
    if (app) app.classList.toggle('loading', busy);
    const generate = root.document && root.document.getElementById('eaReportGenerate');
    if (generate) generate.disabled = busy;
  }

  function setStatus(message, error) {
    const status = root.document && root.document.getElementById('eaReportStatus');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('error', !!error);
  }

  function setExportEnabled(enabled) {
    ['eaReportPdf', 'eaReportExcel', 'eaReportPrint'].forEach(id => {
      const button = root.document && root.document.getElementById(id);
      if (button) button.disabled = !enabled;
    });
  }

  function bindControls() {
    const preset = root.document.getElementById('eaReportPreset');
    const generate = root.document.getElementById('eaReportGenerate');
    const pdf = root.document.getElementById('eaReportPdf');
    const excel = root.document.getElementById('eaReportExcel');
    const print = root.document.getElementById('eaReportPrint');
    if (preset) preset.addEventListener('change', () => setPreset(preset.value));
    if (generate) generate.addEventListener('click', () => generateReport(true));
    if (pdf) pdf.addEventListener('click', exportPDF);
    if (excel) excel.addEventListener('click', exportExcel);
    if (print) print.addEventListener('click', printReport);
    setPreset('this-month');
  }

  function configure(options) {
    if (!root.document || !options || !options.node) return;
    captureOriginalPanel();
    installStyles();
    destroyCharts();
    state.report = null;
    const model = root.EaGroupModel;
    const deviceIds = model && typeof model.collectIncludedDeviceIds === 'function'
      ? model.collectIncludedDeviceIds(options.node)
      : (options.deviceIds || []);
    const entries = model && typeof model.buildDrilldownEntries === 'function'
      ? model.buildDrilldownEntries(options.node, options.deviceGroups)
      : [];
    state.context = {
      node: options.node,
      title: String(options.title || options.node.text || 'Enerji Grubu'),
      deviceGroups: options.deviceGroups || {},
      deviceIds: uniqueStrings(deviceIds),
      entries
    };
    const panel = root.document.getElementById('tablePanel');
    if (!panel) return;
    panel.classList.add('ea-report-mode');
    panel.innerHTML = reportShellHtml();
    bindControls();
  }

  function reset() {
    destroyCharts();
    state.context = null;
    state.report = null;
    state.loadingPromise = null;
    if (!root.document) return;
    const panel = root.document.getElementById('tablePanel');
    if (panel && state.panelCaptured && state.originalPanelHtml !== null) panel.innerHTML = state.originalPanelHtml;
    if (panel) panel.classList.remove('ea-report-mode');
  }

  function activate() {
    if (!state.context || state.report || state.loadingPromise) return state.loadingPromise;
    state.loadingPromise = generateReport(false).finally(() => { state.loadingPromise = null; });
    return state.loadingPromise;
  }

  function reportId(context, from) {
    const scope = String(context.node.id || context.node.data && context.node.data.parentId || 'EA').replace(/[^a-z0-9]/gi, '').slice(-12);
    return `ENP-${String(from).replace(/-/g, '')}-${scope || 'EA'}`;
  }

  async function generateReport(userInitiated) {
    if (!state.context || !root.document) return null;
    const from = root.document.getElementById('eaReportFrom')?.value;
    const to = root.document.getElementById('eaReportTo')?.value;
    const compare = !!root.document.getElementById('eaReportCompare')?.checked;
    const periodDays = daysInclusive(from, to);
    if (!periodDays) {
      setStatus('Geçerli bir başlangıç ve bitiş tarihi seçin.', true);
      return null;
    }
    const model = root.EaGroupModel;
    const definitions = buildDeviceDefinitions(state.context.deviceGroups, state.context.deviceIds, model);
    if (!definitions.length) {
      setStatus('Bu rapor kapsamında enerji analizörü bulunamadı.', true);
      return null;
    }

    setBusy(true);
    setExportEnabled(false);
    setStatus(`${definitions.length} analizör için veriler hazırlanıyor…`, false);
    try {
      const energyTags = uniqueStrings(definitions.map(definition => definition.energyTag));
      const powerTags = uniqueStrings(definitions.flatMap(definition => definition.powerTags));
      const currentFrom = apiDate(from);
      const currentTo = apiDate(dateToInput(addDays(parseInputDate(to), 1)));
      const previousToDate = parseInputDate(from);
      const previousFromDate = addDays(previousToDate, -periodDays);
      const previousFrom = apiDate(dateToInput(previousFromDate));
      const previousTo = apiDate(dateToInput(previousToDate));

      const currentPromise = fetchSummary([...energyTags, ...powerTags], currentFrom, currentTo);
      const previousPromise = compare && energyTags.length ? fetchSummary(energyTags, previousFrom, previousTo) : Promise.resolve({});
      const trendPromise = energyTags.length ? fetchDailyAggregation(energyTags, currentFrom, currentTo) : Promise.resolve([]);
      const [currentSummary, previousSummary, aggregationRows] = await Promise.all([currentPromise, previousPromise, trendPromise]);
      const hasUsableMeterHistory = (definition, summary) => {
        const record = definition.energyTag && summary[definition.energyTag];
        return !!(record && record.diff !== null && record.diff >= 0);
      };
      const powerFallbackDefinitions = definitions.filter(definition => !hasUsableMeterHistory(definition, currentSummary) && definition.powerTags.length);
      const previousFallbackDefinitions = compare
        ? definitions.filter(definition => !hasUsableMeterHistory(definition, previousSummary) && definition.powerTags.length)
        : [];
      const fallbackPowerTags = uniqueStrings(powerFallbackDefinitions.flatMap(definition => definition.powerTags));
      const previousFallbackTags = uniqueStrings(previousFallbackDefinitions.flatMap(definition => definition.powerTags));
      if (fallbackPowerTags.length) setStatus(`${powerFallbackDefinitions.length} analizör için tüketim aktif güç geçmişinden hesaplanıyor…`, false);
      const [powerRows, previousPowerRows] = await Promise.all([
        fallbackPowerTags.length ? fetchHourlyPowerAggregation(fallbackPowerTags, currentFrom, currentTo) : Promise.resolve([]),
        previousFallbackTags.length ? fetchHourlyPowerAggregation(previousFallbackTags, previousFrom, previousTo) : Promise.resolve([])
      ]);
      const meterTrend = buildDailyTrend(aggregationRows, energyTags, periodDays);
      const powerTrend = buildPowerConsumption(powerRows, powerFallbackDefinitions, periodDays);
      const previousPowerTrend = buildPowerConsumption(previousPowerRows, previousFallbackDefinitions, periodDays);
      const metrics = buildDeviceMetrics(definitions, currentSummary, previousSummary, meterTrend.completenessByTag, {
        powerFallbackByDevice: powerTrend.byDevice,
        previousPowerFallbackByDevice: previousPowerTrend.byDevice
      });
      const summary = summarizeReport({ deviceMetrics: metrics, periodDays });
      const breakdown = buildBreakdownRows(state.context.entries, metrics);
      const warnings = [];
      const missingEnergy = metrics.filter(metric => metric.energySource === 'missing').length;
      const resetCount = metrics.filter(metric => metric.resetDetected).length;
      if (summary.estimatedDevices) warnings.push(`${summary.estimatedDevices} analizörde kWh sayaç geçmişi yok; tüketim saatlik ortalama aktif güçten (kW × saat) tahmini hesaplandı.`);
      if (missingEnergy) warnings.push(`${missingEnergy} analizörde ne sayaç geçmişi ne de kullanılabilir aktif güç kaydı bulundu; bu cihazlar toplam tüketime katılmadı.`);
      if (resetCount) warnings.push(`${resetCount} analizörde sayaç sıfırlaması/geri dönüşü tespit edildi; varsa aktif güç geçmişi yedek kaynak olarak kullanıldı.`);
      if (summary.completeness !== null && summary.completeness < 95) warnings.push(`Ortalama veri tamlığı %${formatNumber(summary.completeness, 1)}; eksik günler sonuçları etkileyebilir.`);
      warnings.push('Üretim miktarı veya çalışma saati tagi tanımlı olmadığı için temel EnPI kWh/gün olarak hesaplandı.');

      state.report = {
        id: reportId(state.context, from),
        title: state.context.title,
        from,
        to,
        periodDays,
        previousFrom: dateToInput(previousFromDate),
        previousTo: dateToInput(addDays(previousToDate, -1)),
        compare,
        generatedAt: new Date(),
        deviceMetrics: metrics,
        summary,
        breakdown,
        trend: mergeTrendPoints(meterTrend.points, powerTrend.points),
        warnings
      };
      renderReport(state.report);
      setExportEnabled(true);
      setStatus(`Rapor hazırlandı: ${formatDate(from)} – ${formatDate(to)} · ${summary.availableEnergyDevices}/${summary.deviceCount} analizör (${summary.meterDevices} sayaç, ${summary.estimatedDevices} güç hesabı)`, false);
      if (userInitiated) showToast('Enerji performans raporu hazırlandı', { type: 'success', duration: 2200 });
      return state.report;
    } catch (error) {
      console.error('[EA Report] Rapor oluşturulamadı:', error);
      state.report = null;
      setStatus(`Rapor oluşturulamadı: ${error.message || error}`, true);
      showToast('Enerji raporu oluşturulamadı', { type: 'danger', duration: 3000 });
      return null;
    } finally {
      setBusy(false);
    }
  }

  function changeClass(change) {
    if (change === null || !Number.isFinite(change)) return '';
    return change <= 0 ? 'good' : 'bad';
  }

  function renderReport(report) {
    const container = root.document.getElementById('eaReportDocument');
    if (!container) return;
    destroyCharts();
    const peak = report.summary.peakDevice;
    const comparisonText = report.summary.changePercent === null
      ? 'Karşılaştırma verisi yok'
      : `${report.summary.changePercent > 0 ? '+' : ''}${formatNumber(report.summary.changePercent, 1)}% önceki dönem`;
    const warningHtml = report.warnings.map(message => `<div class="ea-report-warning"><strong>!</strong><span>${escapeHtml(message)}</span></div>`).join('');
    const breakdownRows = report.breakdown.map(row => `
      <tr><td>${escapeHtml(row.label)}</td><td class="num">${row.deviceCount}</td><td class="num">${formatNumber(row.consumptionKwh, 1)}</td><td class="num">${formatNumber(row.percent, 1)}%</td></tr>`).join('');
    const deviceRows = report.deviceMetrics.map(metric => {
      const status = metric.energySource === 'power'
        ? 'Güçten tahmini'
        : metric.resetDetected
          ? 'Sayaç kontrolü'
          : metric.missingEnergyTag
            ? 'Enerji tagi yok'
            : metric.consumptionKwh === null
              ? 'Veri yok'
              : 'Sayaç';
      return `<tr>
        <td>${escapeHtml(metric.label)}<br><small>${escapeHtml(metric.code)} · ID ${escapeHtml(metric.deviceId)}</small></td>
        <td class="num">${formatNumber(metric.startIndex, 1)}</td>
        <td class="num">${formatNumber(metric.endIndex, 1)}</td>
        <td class="num"><strong>${formatNumber(metric.consumptionKwh, 1)}</strong></td>
        <td class="num">${formatNumber(metric.averagePowerKw, 2)}</td>
        <td class="num">${formatNumber(metric.peakPowerKw, 2)}</td>
        <td class="num">${metric.completeness === null ? '—' : `${formatNumber(metric.completeness, 0)}%`}</td>
        <td>${escapeHtml(status)}</td>
      </tr>`;
    }).join('');

    container.innerHTML = `
      <header class="ea-report-head">
        <div><div class="ea-report-brand">DATARAPOR · ENERJİ YÖNETİMİ</div><h1>Enerji Performans Raporu</h1><div class="ea-report-scope">${escapeHtml(report.title)}</div></div>
        <div class="ea-report-badge">ISO 50001 ODAKLI<br>PERFORMANS İZLEME</div>
      </header>
      <div class="ea-report-meta">
        <div><span>Rapor No</span><strong>${escapeHtml(report.id)}</strong></div>
        <div><span>Rapor Dönemi</span><strong>${formatDate(report.from)} – ${formatDate(report.to)}</strong></div>
        <div><span>Kapsam</span><strong>${report.summary.deviceCount} enerji analizörü</strong></div>
        <div><span>Oluşturulma</span><strong>${escapeHtml(report.generatedAt.toLocaleString('tr-TR'))}</strong></div>
      </div>
      <section class="ea-report-section">
        <h2>Yönetici Özeti</h2>
        <div class="ea-report-kpis">
          <div class="ea-report-kpi"><span>Toplam Tüketim</span><strong>${formatNumber(report.summary.totalKwh, 1)} kWh</strong><small>${report.summary.meterDevices} sayaç + ${report.summary.estimatedDevices} güç hesabı</small></div>
          <div class="ea-report-kpi ${changeClass(report.summary.changePercent)}"><span>Dönemsel Değişim</span><strong>${report.summary.changePercent === null ? '—' : `${report.summary.changePercent > 0 ? '+' : ''}${formatNumber(report.summary.changePercent, 1)}%`}</strong><small>${escapeHtml(comparisonText)}</small></div>
          <div class="ea-report-kpi"><span>Temel EnPI</span><strong>${formatNumber(report.summary.enpiKwhPerDay, 1)} kWh/gün</strong><small>${report.periodDays} takvim günü</small></div>
          <div class="ea-report-kpi"><span>Veri Tamlığı</span><strong>${report.summary.completeness === null ? '—' : `${formatNumber(report.summary.completeness, 1)}%`}</strong><small>Sayaç / aktif güç kayıtları</small></div>
        </div>
        <div class="ea-report-kpis" style="margin-top:8px">
          <div class="ea-report-kpi"><span>Toplam Ortalama Güç</span><strong>${formatNumber(report.summary.averagePowerKw, 2)} kW</strong><small>EA ortalamalarının toplamı</small></div>
          <div class="ea-report-kpi"><span>En Yüksek EA Gücü</span><strong>${peak ? `${formatNumber(peak.peakPowerKw, 2)} kW` : '—'}</strong><small>${peak ? escapeHtml(peak.label) : 'Güç verisi yok'}</small></div>
          <div class="ea-report-kpi"><span>Referans Dönem</span><strong>${report.compare ? `${formatDate(report.previousFrom)} – ${formatDate(report.previousTo)}` : 'Kapalı'}</strong><small>${report.compare ? `${formatNumber(report.summary.previousKwh, 1)} kWh` : 'Karşılaştırma yapılmadı'}</small></div>
          <div class="ea-report-kpi"><span>Rapor Kapsamı</span><strong>${report.breakdown.length} alt kırılım</strong><small>Seçili Node ayarlarına göre</small></div>
        </div>
      </section>
      <section class="ea-report-section">
        <h2>Enerji Eğilimi ve Dağılımı</h2>
        <div class="ea-report-chart-grid">
          <div class="ea-report-chart-card"><h3>Günlük tüketim (kWh)</h3><div class="ea-report-chart-wrap"><canvas id="eaReportTrendChart"></canvas></div></div>
          <div class="ea-report-chart-card"><h3>Alt grup tüketim payı</h3><div class="ea-report-chart-wrap"><canvas id="eaReportBreakdownChart"></canvas></div></div>
        </div>
      </section>
      <section class="ea-report-section">
        <h2>Alt Grup Özeti</h2>
        <table class="ea-report-table"><thead><tr><th>Alt grup</th><th class="num">EA</th><th class="num">Tüketim (kWh)</th><th class="num">Pay</th></tr></thead><tbody>${breakdownRows || '<tr><td colspan="4">Alt grup verisi yok.</td></tr>'}</tbody></table>
      </section>
      <section class="ea-report-section">
        <h2>Enerji Analizörü Detayları</h2>
        <table class="ea-report-table"><thead><tr><th>Analizör</th><th class="num">İlk endeks</th><th class="num">Son endeks</th><th class="num">Tüketim (kWh)</th><th class="num">Ort. kW</th><th class="num">Maks. kW</th><th class="num">Tamlık</th><th>Durum</th></tr></thead><tbody>${deviceRows}</tbody></table>
      </section>
      <section class="ea-report-section"><h2>Veri Kalitesi ve Yöntem</h2>${warningHtml}<div class="ea-report-note">Tüketim öncelikle enerji sayaçlarının dönem başlangıç ve bitiş endeks farkından hesaplanır. Sayaç geçmişi olmayan cihazlarda saatlik ortalama aktif güç değerleri kW × saat yöntemiyle entegre edilir ve sonuç tahmini olarak işaretlenir. DataMap'te watt olarak tanımlı güçler otomatik kW'a çevrilir. Alt grup kapsamı Node Ayarları'ndaki seçimleri izler; aynı Device ID toplam raporda bir kez sayılır. Bu belge ISO 50001 enerji performansı izleme çalışmalarını destekler; tek başına uygunluk veya sertifikasyon belgesi değildir.</div></section>
      <div class="ea-report-signatures"><div class="ea-report-signature">Hazırlayan</div><div class="ea-report-signature">Kontrol Eden</div><div class="ea-report-signature">Onaylayan</div></div>
      <footer class="ea-report-footer"><span>DataRapor Enerji Yönetimi</span><span>${escapeHtml(report.id)} · ISO 50001 odaklı izleme</span></footer>`;
    renderCharts(report);
  }

  function renderCharts(report) {
    if (typeof root.Chart !== 'function') return;
    const trendCanvas = root.document.getElementById('eaReportTrendChart');
    const breakdownCanvas = root.document.getElementById('eaReportBreakdownChart');
    if (trendCanvas) {
      state.charts.push(new root.Chart(trendCanvas, {
        type: 'line',
        data: {
          labels: report.trend.map(point => formatDate(point.date)),
          datasets: [{ label: 'Tüketim (kWh)', data: report.trend.map(point => point.value), borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,.13)', fill: true, tension: .25, pointRadius: report.trend.length > 20 ? 0 : 2 }]
        },
        options: { responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: '#607086', maxRotation: 45, font: { size: 8 } }, grid: { display: false } }, y: { beginAtZero: true, ticks: { color: '#607086', font: { size: 8 } }, grid: { color: '#e6ebf1' } } } }
      }));
    }
    if (breakdownCanvas) {
      const usable = report.breakdown
        .filter(row => row.consumptionKwh !== null && row.consumptionKwh > 0)
        .sort((left, right) => right.consumptionKwh - left.consumptionKwh);
      let chartRows = usable;
      if (usable.length > 9) {
        const others = usable.slice(8);
        chartRows = usable.slice(0, 8).concat([{
          label: `Diğer ${others.length} alt grup`,
          consumptionKwh: others.reduce((sum, row) => sum + row.consumptionKwh, 0),
          color: '#94a3b8'
        }]);
      }
      state.charts.push(new root.Chart(breakdownCanvas, {
        type: 'doughnut',
        data: { labels: chartRows.map(row => row.label), datasets: [{ data: chartRows.map(row => row.consumptionKwh), backgroundColor: chartRows.map(row => row.color), borderColor: '#fff', borderWidth: 2 }] },
        options: { responsive: true, maintainAspectRatio: false, animation: false, cutout: '58%', plugins: { legend: { position: 'bottom', labels: { color: '#44556b', boxWidth: 9, font: { size: 8 } } }, tooltip: { callbacks: { label: context => ` ${context.label}: ${formatNumber(context.raw, 1)} kWh` } } } }
      }));
    }
  }

  function safeFilename(value) {
    return String(value || 'enerji-raporu').toLocaleLowerCase('tr-TR').replace(/[çÇ]/g, 'c').replace(/[ğĞ]/g, 'g').replace(/[ıİ]/g, 'i').replace(/[öÖ]/g, 'o').replace(/[şŞ]/g, 's').replace(/[üÜ]/g, 'u').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
  }

  function cloneReportElement(source) {
    const clone = source.cloneNode(true);
    const sourceCanvases = source.querySelectorAll('canvas');
    const cloneCanvases = clone.querySelectorAll('canvas');
    sourceCanvases.forEach((canvas, index) => {
      try {
        const image = root.document.createElement('img');
        image.src = canvas.toDataURL('image/png');
        image.style.width = '100%';
        image.style.height = '100%';
        image.style.objectFit = 'contain';
        cloneCanvases[index].replaceWith(image);
      } catch (_) { /* Chart export is optional. */ }
    });
    return clone;
  }

  function buildPdfPages(report, source) {
    if (!root.document || !report || !source) return null;
    const host = root.document.createElement('div');
    host.className = 'ea-report-pdf-host';
    root.document.body.appendChild(host);
    const pages = [];

    function createPage() {
      const page = root.document.createElement('article');
      page.className = 'ea-report-document ea-report-pdf-page';
      const content = root.document.createElement('div');
      content.className = 'ea-report-pdf-content';
      if (pages.length) {
        const heading = root.document.createElement('div');
        heading.className = 'ea-report-page-heading';
        heading.innerHTML = `<span>${escapeHtml(report.title)} · Enerji Performans Raporu</span><span>${escapeHtml(report.id)}</span>`;
        content.appendChild(heading);
      }
      const footer = root.document.createElement('footer');
      footer.className = 'ea-report-page-footer';
      footer.innerHTML = `<span>DataRapor Enerji Yönetimi</span><span>${escapeHtml(report.id)} · ISO 50001 odaklı izleme</span><span>Sayfa ${pages.length + 1}</span>`;
      page.appendChild(content);
      page.appendChild(footer);
      host.appendChild(page);
      const entry = { element: page, content, footer };
      pages.push(entry);
      return entry;
    }

    function pageFits(page) {
      const contentBounds = page.content.getBoundingClientRect();
      const footerBounds = page.footer.getBoundingClientRect();
      return contentBounds.bottom <= footerBounds.top - 12;
    }

    function pageHasReportContent(page) {
      return [...page.content.children].some(child => !child.classList.contains('ea-report-page-heading'));
    }

    let currentPage = createPage();

    function appendSection(sourceSection) {
      const clone = cloneReportElement(sourceSection);
      currentPage.content.appendChild(clone);
      if (!pageFits(currentPage) && pageHasReportContent(currentPage) && currentPage.content.children.length > 1) {
        clone.remove();
        currentPage = createPage();
        currentPage.content.appendChild(clone);
      }
    }

    function appendTableSection(sourceSection) {
      if (pageHasReportContent(currentPage)) currentPage = createPage();
      const sourceTable = sourceSection.querySelector('.ea-report-table');
      const sourceHeading = sourceSection.querySelector('h2');
      const sourceRows = sourceTable.querySelectorAll('tbody tr');
      let continued = false;
      let fragment = null;

      function createFragment() {
        const section = sourceSection.cloneNode(false);
        if (sourceHeading) {
          const heading = sourceHeading.cloneNode(true);
          if (continued) heading.textContent += ' (devam)';
          section.appendChild(heading);
        }
        const table = sourceTable.cloneNode(false);
        const sourceThead = sourceTable.querySelector('thead');
        if (sourceThead) table.appendChild(sourceThead.cloneNode(true));
        const body = root.document.createElement('tbody');
        table.appendChild(body);
        section.appendChild(table);
        currentPage.content.appendChild(section);
        fragment = { section, body };
      }

      createFragment();
      sourceRows.forEach(sourceRow => {
        const row = sourceRow.cloneNode(true);
        fragment.body.appendChild(row);
        if (!pageFits(currentPage) && fragment.body.children.length > 1) {
          row.remove();
          currentPage = createPage();
          continued = true;
          createFragment();
          fragment.body.appendChild(row);
        }
      });
    }

    [...source.children].forEach(child => {
      if (child.classList.contains('ea-report-footer')) return;
      if (child.classList.contains('ea-report-section') && child.querySelector('.ea-report-table')) {
        appendTableSection(child);
      } else {
        appendSection(child);
      }
    });

    pages.forEach((page, index) => {
      page.footer.innerHTML = `<span>DataRapor Enerji Yönetimi</span><span>${escapeHtml(report.id)} · ISO 50001 odaklı izleme</span><span>Sayfa ${index + 1} / ${pages.length}</span>`;
    });

    return { host, pages };
  }

  async function exportPDF() {
    if (!state.report || !root.document) return;
    if (typeof root.html2canvas !== 'function' || !root.jspdf || !root.jspdf.jsPDF) {
      showToast('PDF kütüphanesi yüklenemedi', { type: 'danger', duration: 2500 });
      return;
    }
    const documentElement = root.document.getElementById('eaReportDocument');
    const button = root.document.getElementById('eaReportPdf');
    if (!documentElement) return;
    if (button) button.disabled = true;
    setStatus('PDF hazırlanıyor…', false);
    let layout = null;
    try {
      layout = buildPdfPages(state.report, documentElement);
      await Promise.all([...layout.host.querySelectorAll('img')].map(image => typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve()));
      const { jsPDF } = root.jspdf;
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
      for (let index = 0; index < layout.pages.length; index += 1) {
        setStatus(`PDF hazırlanıyor… ${index + 1}/${layout.pages.length}`, false);
        const canvas = await root.html2canvas(layout.pages[index].element, { scale: 1.7, backgroundColor: '#ffffff', useCORS: true, logging: false });
        if (index > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/jpeg', .92), 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
      }
      pdf.setProperties({ title: `Enerji Performans Raporu - ${state.report.title}`, subject: 'ISO 50001 odaklı enerji performansı izleme raporu', author: 'DataRapor' });
      pdf.save(`${safeFilename(state.report.title)}-enerji-raporu-${state.report.from}-${state.report.to}.pdf`);
      setStatus('PDF indirildi.', false);
      showToast('PDF indirildi', { type: 'success', duration: 2200 });
    } catch (error) {
      console.error('[EA Report] PDF error:', error);
      setStatus(`PDF oluşturulamadı: ${error.message || error}`, true);
    } finally {
      if (layout && layout.host) layout.host.remove();
      if (button) button.disabled = false;
    }
  }

  function exportExcel() {
    if (!state.report || !root.XLSX) {
      showToast('Excel kütüphanesi yüklenemedi', { type: 'danger', duration: 2500 });
      return;
    }
    const report = state.report;
    const wb = root.XLSX.utils.book_new();
    const summaryRows = [
      ['Enerji Performans Raporu'], ['Kapsam', report.title], ['Rapor No', report.id], ['Dönem', `${report.from} - ${report.to}`],
      [], ['Gösterge', 'Değer', 'Birim'], ['Toplam Tüketim', report.summary.totalKwh, 'kWh'], ['Önceki Dönem', report.summary.previousKwh, 'kWh'],
      ['Dönemsel Değişim', report.summary.changePercent, '%'], ['Temel EnPI', report.summary.enpiKwhPerDay, 'kWh/gün'], ['Toplam Ortalama Güç', report.summary.averagePowerKw, 'kW'], ['Veri Tamlığı', report.summary.completeness, '%']
    ];
    const wsSummary = root.XLSX.utils.aoa_to_sheet(summaryRows);
    wsSummary['!cols'] = [{ wch: 26 }, { wch: 30 }, { wch: 14 }];
    root.XLSX.utils.book_append_sheet(wb, wsSummary, 'Özet');

    const detailRows = [['Cihaz ID', 'Analizör', 'Kod', 'Enerji Tag', 'İlk Endeks', 'Son Endeks', 'Tüketim kWh', 'Ortalama kW', 'Maksimum kW', 'Veri Tamlığı %', 'Hesaplama Kaynağı']];
    report.deviceMetrics.forEach(metric => detailRows.push([metric.deviceId, metric.label, metric.code, metric.energyTag || '', metric.startIndex, metric.endIndex, metric.consumptionKwh, metric.averagePowerKw, metric.peakPowerKw, metric.completeness, metric.energySource === 'power' ? 'Saatlik aktif güçten tahmini' : metric.energySource === 'meter' ? 'Enerji sayacı endeks farkı' : 'Veri yok']));
    const wsDetail = root.XLSX.utils.aoa_to_sheet(detailRows);
    wsDetail['!cols'] = [{ wch: 11 }, { wch: 30 }, { wch: 18 }, { wch: 24 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 28 }];
    root.XLSX.utils.book_append_sheet(wb, wsDetail, 'Analizör Detayı');

    const groupRows = [['Alt Grup', 'Analizör Sayısı', 'Tüketim kWh', 'Pay %']];
    report.breakdown.forEach(row => groupRows.push([row.label, row.deviceCount, row.consumptionKwh, row.percent]));
    root.XLSX.utils.book_append_sheet(wb, root.XLSX.utils.aoa_to_sheet(groupRows), 'Alt Gruplar');
    const trendRows = [['Tarih', 'Tüketim kWh'], ...report.trend.map(point => [point.date, point.value])];
    root.XLSX.utils.book_append_sheet(wb, root.XLSX.utils.aoa_to_sheet(trendRows), 'Günlük Trend');
    root.XLSX.writeFile(wb, `${safeFilename(report.title)}-enerji-raporu-${report.from}-${report.to}.xlsx`);
    showToast('Excel raporu indirildi', { type: 'success', duration: 2200 });
  }

  function printReport() {
    if (!state.report || !root.document) return;
    const source = root.document.getElementById('eaReportDocument');
    if (!source) return;
    const style = root.document.getElementById('eaEnergyReportStyles');
    const printWindow = root.open('', '_blank');
    if (!printWindow) {
      showToast('Yazdırma penceresine izin verilmedi', { type: 'warning', duration: 2500 });
      return;
    }
    const layout = buildPdfPages(state.report, source);
    const pagesHtml = layout.pages.map(page => page.element.outerHTML).join('');
    layout.host.remove();
    printWindow.document.write(`<!doctype html><html lang="tr"><head><meta charset="utf-8"><title>${escapeHtml(state.report.title)} - Enerji Raporu</title><style>${style ? style.textContent : ''} body{margin:0;background:#fff}.ea-report-document{width:210mm;box-shadow:none;margin:0 auto}@page{size:A4 portrait;margin:0}</style></head><body>${pagesHtml}<script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script></body></html>`);
    printWindow.document.close();
  }

  if (root.document) {
    root.document.addEventListener('DOMContentLoaded', () => {
      captureOriginalPanel();
      root.document.addEventListener('click', event => {
        const target = event.target && event.target.closest ? event.target.closest('[data-panel="tablePanel"]') : null;
        if (target) root.setTimeout(() => activate(), 20);
      });
    });
  }

  return {
    activate,
    buildBreakdownRows,
    buildDailyTrend,
    buildDeviceDefinitions,
    buildDeviceMetrics,
    buildPdfPages,
    buildPowerConsumption,
    configure,
    daysInclusive,
    generateReport,
    normalizeAggregationRows,
    normalizeSummaryPayload,
    mergeTrendPoints,
    reset,
    summarizeReport
  };
});
